import { useState, useEffect } from 'react';
import { useApiService } from '@/hooks/useApiService';
import { ProjectCore } from '../types';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useTranslation } from 'react-i18next';

interface SelectProjectProps {
    currentSelectedProject?: ProjectCore;
    onProjectChange: (project: ProjectCore) => void;
    formationId: string;
    versionId: string;
    versionAccountStatusId: string;
}

function SelectProject({ currentSelectedProject, onProjectChange, versionAccountStatusId }: SelectProjectProps) {
    const { getProjects, changeVersionAccountStatusProject } = useApiService();
    const [projects, setProjects] = useState<ProjectCore[]>([]);
    const [selectedProject, setSelectedProject] = useState<ProjectCore | undefined>();
    const [projectsUnavailable, setProjectsUnavailable] = useState<boolean>(false);

    const [showAlert, setShowAlert] = useState(false);
    const { t } = useTranslation();

    useEffect(() => {
        setSelectedProject(currentSelectedProject);
    }, [currentSelectedProject]);

    useEffect(() => {
        const fetchData = async () => {
            const projects = await getProjects();
            const filteredProjects = projects.filter(project => project.properties.name);
            if (filteredProjects.length === 0) {
                setProjectsUnavailable(true);
            }

            setProjects(filteredProjects);
        };

        fetchData().catch(console.error);
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleProjectChange = (project: ProjectCore) => {
        setSelectedProject(project);
        setTimeout(() => {
            setShowAlert(true);
        }, 600);
    };
    const handleConfirmChange = () => {
        if (selectedProject && selectedProject._id !== currentSelectedProject?._id) {
            changeVersionAccountStatusProject(versionAccountStatusId, selectedProject._id);

            onProjectChange(selectedProject);
        }

        setShowAlert(false);
    };

    if (projects.length === 0 || projectsUnavailable) {
        return <div>{t('school_no_project')}</div>;
    }
    return (
        <>
            <Select
                onValueChange={value => {
                    const project = projects.find(project => project._id === value);
                    if (project) {
                        handleProjectChange(project);
                    }
                }}
            >
                <SelectTrigger className="w-full">
                    <SelectValue placeholder={selectedProject?.properties?.name || 'Select a project'} />
                </SelectTrigger>
                <SelectContent>
                    {projects.map(project => (
                        <SelectItem key={project._id} value={project._id} className="w-full">
                            {project.properties.name}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>

            <AlertDialog open={showAlert} onOpenChange={setShowAlert}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('school_confirm_project_change')}</AlertDialogTitle>
                        <AlertDialogDescription>{t('school_project_change_explanation')}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={() => setShowAlert(false)}>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleConfirmChange}>
                            {t('school_validate_project_change')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}

export default SelectProject;
